/**
 * Authoritative saved-lead state (Stage Z).
 *
 * Why a module-level store instead of local React state: the same product appears
 * on Auto Sourcing, Saved Leads, Product Analyzer and the sidebar count. Local
 * state per page is exactly how the app previously showed a bookmark as "saved"
 * while nothing was persisted.
 *
 * Rules enforced here:
 *  * A card becomes SAVED only after the API confirms — never on click.
 *  * A failed save restores the previous state and surfaces the real error.
 *  * Repeated clicks are de-duplicated per key (no duplicate requests, no
 *    duplicate leads — the backend upsert is idempotent as well).
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  fetchSavedStatus,
  saveLead,
  savedLeadKey,
  unsaveLead,
} from "./savedLeads";

const listeners = new Set();

/** key → true (confirmed saved) */
let savedKeys = new Set();
/** key → true (request in flight) */
let pendingKeys = new Set();
/** Total saved leads, used by the sidebar badge. */
let savedCount = 0;
let statusLoaded = false;
let lastError = null;

const EMPTY = Object.freeze({
  savedKeys: new Set(),
  pendingKeys: new Set(),
  count: 0,
  loaded: false,
  error: null,
});

let snapshot = EMPTY;

function rebuild() {
  snapshot = Object.freeze({
    savedKeys: new Set(savedKeys),
    pendingKeys: new Set(pendingKeys),
    count: savedCount,
    loaded: statusLoaded,
    error: lastError,
  });
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot() {
  return snapshot;
}

/** Test seam: reset the module store between tests. */
export function __resetSavedLeadsStore() {
  savedKeys = new Set();
  pendingKeys = new Set();
  savedCount = 0;
  statusLoaded = false;
  lastError = null;
  rebuild();
}

function setSaved(key, on) {
  const next = new Set(savedKeys);
  if (on) next.add(key);
  else next.delete(key);
  savedKeys = next;
}

function setPending(key, on) {
  const next = new Set(pendingKeys);
  if (on) next.add(key);
  else next.delete(key);
  pendingKeys = next;
}

/** Human-readable, actionable error text from an axios-or-Error rejection. */
export function savedLeadErrorMessage(err) {
  const detail = err?.response?.data?.detail ?? err?.detail;
  if (typeof detail === "string" && detail.trim()) {
    if (detail === "invalid_walmart_item_id") {
      return "This product has no matched Walmart listing yet, so it cannot be saved.";
    }
    return detail.replace(/_/g, " ");
  }
  if (err?.response?.status === 401 || err?.status === 401) {
    return "Your session expired. Sign in again to save this product.";
  }
  if (err?.response?.status === 403) {
    return "You do not have permission to save products in this workspace.";
  }
  return err?.message || "Could not save this product. Please try again.";
}

/**
 * Hydrate saved state for the given leads in ONE request.
 * Safe to call repeatedly; it only adds confirmed keys.
 */
export async function hydrateSavedStatus(leads = []) {
  const keys = [];
  const byItem = new Map();
  for (const lead of leads) {
    const itemId = String(lead?.walmartItemId ?? lead?.walmart_item_id ?? "").trim();
    const key = savedLeadKey({
      walmartItemId: itemId,
      sourceId: lead?.sourceId ?? lead?.source_id,
      sourceProductId: lead?.sourceProductId ?? lead?.source_product_id,
    });
    if (!itemId || !key) continue;
    keys.push(key);
    if (!byItem.has(itemId)) byItem.set(itemId, []);
    byItem.get(itemId).push(key);
  }
  if (keys.length === 0) return getSnapshot();

  try {
    const res = await fetchSavedStatus([...byItem.keys()]);
    const savedItemIds = res?.data?.saved || res?.saved || {};
    const next = new Set(savedKeys);
    for (const [itemId, keyList] of byItem) {
      if (savedItemIds[itemId]) {
        // The API reports item-level saved state; mark every key for that item.
        keyList.forEach((k) => next.add(k));
      }
    }
    savedKeys = next;
    statusLoaded = true;
    rebuild();
  } catch (err) {
    lastError = savedLeadErrorMessage(err);
    rebuild();
  }
  return getSnapshot();
}


/** Refresh the total saved count (sidebar badge). */
export async function refreshSavedCount() {
  try {
    const { listSavedLeads } = await import("./savedLeads");
    const res = await listSavedLeads(1000);
    const items = res?.data?.items || res?.items || [];
    savedCount = Array.isArray(items) ? items.length : 0;
    rebuild();
  } catch {
    /* count is a convenience — never break the page */
  }
  return savedCount;
}

/**
 * Save or unsave one lead.
 *
 * Returns { ok, saved, error }. The caller updates nothing itself — the store
 * drives every view, and failure restores the previous state.
 */
export async function toggleSavedLead(lead, { nextSaved } = {}) {
  const itemId = String(lead?.walmartItemId ?? lead?.walmart_item_id ?? "").trim();
  const sourceKey =
    lead?.sourceId && lead?.sourceProductId
      ? `${lead.sourceId}:${lead.sourceProductId}`
      : null;
  const key = savedLeadKey({
    walmartItemId: itemId,
    sourceId: lead?.sourceId ?? lead?.source_id,
    sourceProductId: lead?.sourceProductId ?? lead?.source_product_id,
  });
  if (!key) {
    const error = "This product has no matched Walmart listing yet, so it cannot be saved.";
    return { ok: false, saved: false, error };
  }
  // De-duplicate concurrent clicks on the same key.
  if (pendingKeys.has(key)) {
    return { ok: true, saved: savedKeys.has(key), pending: true };
  }

  const wasSaved = savedKeys.has(key);
  const target = typeof nextSaved === "boolean" ? nextSaved : !wasSaved;

  setPending(key, true);
  lastError = null;
  rebuild();
  try {
    if (target) {
      await saveLead(lead);
      setSaved(key, true);
      savedCount += wasSaved ? 0 : 1;
    } else {
      await unsaveLead({ walmartItemId: itemId, sourceKey });
      setSaved(key, false);
      savedCount = Math.max(0, savedCount - (wasSaved ? 1 : 0));
    }
    return { ok: true, saved: target };
  } catch (err) {
    // Restore the previous state — never leave a false "saved".
    setSaved(key, wasSaved);
    lastError = savedLeadErrorMessage(err);
    return { ok: false, saved: wasSaved, error: lastError };
  } finally {
    setPending(key, false);
    rebuild();
  }
}

/** Subscribe to saved state for a set of leads. */
export function useSavedLeads(leads = []) {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Hydrate once per distinct key set.
  const signature = leads
    .map((l) =>
      savedLeadKey({
        walmartItemId: l?.walmartItemId ?? l?.walmart_item_id,
        sourceId: l?.sourceId ?? l?.source_id,
        sourceProductId: l?.sourceProductId ?? l?.source_product_id,
      }),
    )
    .filter(Boolean)
    .sort()
    .join("|");

  useEffect(() => {
    if (!signature) return;
    void hydrateSavedStatus(leads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const isSaved = useCallback(
    (lead) =>
      state.savedKeys.has(
        savedLeadKey({
          walmartItemId: lead?.walmartItemId ?? lead?.walmart_item_id,
          sourceId: lead?.sourceId ?? lead?.source_id,
          sourceProductId: lead?.sourceProductId ?? lead?.source_product_id,
        }),
      ),
    [state.savedKeys],
  );

  const isPending = useCallback(
    (lead) =>
      state.pendingKeys.has(
        savedLeadKey({
          walmartItemId: lead?.walmartItemId ?? lead?.walmart_item_id,
          sourceId: lead?.sourceId ?? lead?.source_id,
          sourceProductId: lead?.sourceProductId ?? lead?.source_product_id,
        }),
      ),
    [state.pendingKeys],
  );

  return { ...state, isSaved, isPending, toggle: toggleSavedLead };
}


/**
 * Live saved-lead count for the sidebar badge.
 *
 * Subscribes to the same store as every card, so saving on any page updates the
 * badge without a reload.
 */
export function useSavedLeadsCount({ hydrate = true } = {}) {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    if (hydrate) void refreshSavedCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrate]);
  return state.count;
}
